import type { CanvasSectionBlock, CanvasSectionBlockType } from '../sections/sectionRegistry';
import type { FlowDefinition, FlowNode, FlowValueType } from '../../types';
import {
  FLOW_NODE_DEFINITIONS,
  getFlowNodeDefinition,
  resolveFlowNodeSocketType,
  type FlowNodeDefinition,
  type FlowNodeSocketDefinition,
} from '../../lib/flowNodeDefinitions';

export type ConnectedBlockType =
  | 'gallery'
  | 'product-card'
  | 'product-deconstructor'
  | 'customiser-viewer'
  | 'customiser-finish-picker'
  | 'customiser-colour-filter'
  | 'customiser-combination-results';

export type ConnectedBlockSocketDefinition = FlowNodeSocketDefinition;

export interface ConnectedBlockDefinition
  extends Pick<FlowNodeDefinition, 'type' | 'label' | 'description' | 'tier' | 'sockets'> {}

export interface ConnectedBlockNodeSummary {
  flow: FlowDefinition;
  node: FlowNode;
  inputs: Array<{ socket: ConnectedBlockSocketDefinition; fieldLabel: string; fieldType: FlowValueType }>;
  outputs: Array<{ socket: ConnectedBlockSocketDefinition; fieldLabel: string; fieldType: FlowValueType }>;
}

export const CONNECTED_BLOCK_DEFINITIONS: Record<ConnectedBlockType, ConnectedBlockDefinition> = {
  gallery: FLOW_NODE_DEFINITIONS.gallery,
  'product-card': FLOW_NODE_DEFINITIONS['product-card'],
  'product-deconstructor': FLOW_NODE_DEFINITIONS['product-deconstructor'],
  'customiser-viewer': FLOW_NODE_DEFINITIONS['customiser-viewer'],
  'customiser-finish-picker': FLOW_NODE_DEFINITIONS['customiser-finish-picker'],
  'customiser-colour-filter': FLOW_NODE_DEFINITIONS['customiser-colour-filter'],
  'customiser-combination-results': FLOW_NODE_DEFINITIONS['customiser-combination-results'],
};

export const CONNECTED_BLOCK_TYPE_OPTIONS: Array<{ value: ConnectedBlockType; label: string }> = Object.values(
  CONNECTED_BLOCK_DEFINITIONS
).map((definition) => ({
  value: definition.type as ConnectedBlockType,
  label: definition.label,
}));

export function isConnectedBlockType(type: CanvasSectionBlockType | string | null | undefined): type is ConnectedBlockType {
  if (!type) return false;
  return type in CONNECTED_BLOCK_DEFINITIONS;
}

export function getConnectedBlockDefinition(type: CanvasSectionBlockType | string | null | undefined): ConnectedBlockDefinition | null {
  if (!isConnectedBlockType(type)) return null;
  return CONNECTED_BLOCK_DEFINITIONS[type];
}

export function findConnectedBlockNodeSummary(
  block: Pick<CanvasSectionBlock, 'id' | 'type'> | null | undefined,
  flows: FlowDefinition[] | null | undefined
): ConnectedBlockNodeSummary | null {
  if (!block || !isConnectedBlockType(block.type) || !Array.isArray(flows)) return null;
  for (const flow of flows) {
    const summary = findConnectedBlockNodeSummaryInFlow(block, flow);
    if (summary) return summary;
  }

  return null;
}

export function findConnectedBlockNodeSummaryInFlow(
  block: Pick<CanvasSectionBlock, 'id' | 'type'> | null | undefined,
  flow: FlowDefinition | null | undefined
): ConnectedBlockNodeSummary | null {
  if (!block || !isConnectedBlockType(block.type) || !flow) return null;
  const node = flow.nodes.find((entry) => entry.blockId === block.id);
  if (!node) return null;
  const definition = getConnectedBlockDefinition(block.type);
  if (!definition) return null;
  const fieldById = new Map(flow.fields.map((field) => [field.id, field]));
  const summarize = (direction: 'input' | 'output') =>
    definition.sockets
      .filter((socket) => socket.direction === direction)
      .map((socket) => {
        const binding = (direction === 'input' ? node.inputs : node.outputs).find(
          (entry) => entry.socketId === socket.id
        );
        const field = binding ? fieldById.get(binding.fieldId) : null;
        return {
          socket,
          fieldLabel: field?.label ?? 'Not connected',
          fieldType:
            field?.type ??
            resolveFlowNodeSocketType(node, socket.id, flow) ??
            socket.type ??
            'scalar.string',
        };
      });

  return {
    flow,
    node,
    inputs: summarize('input'),
    outputs: summarize('output'),
  };
}

export function isBlockBackedFlowNodeType(type: string | null | undefined) {
  return Boolean(type && getFlowNodeDefinition(type)?.kind === 'block');
}
